<script setup lang="ts">
  import { computed, inject } from 'vue';
  import { useBranchLogo } from '@/entities/Branch';
  import { isNodeShown, TABLE_NEW_ROWS_LIMIT_MESSAGE, type CompiledNode, type FieldValue } from '@record-fill';
  import type { ElementProperties } from '../../model/form.types';
  import { Tooltip } from '@/shared/ui';
  import { useElementContainerStyles } from '../../model/useElementContainerStyles';
  import { extraRowsGrid, fillGridStyles, multipleTableBarRowStyles, type ExtraGridCell } from './fill-table-styles';
  import { extraCellChange, type ChangeHighlight } from './change-marks';
  import FillFieldNode from './FillFieldNode.vue';
  import FillImageNode from './FillImageNode.vue';
  import FillRemoveRowButton from './FillRemoveRowButton.vue';
  import FillStaticNode from './FillStaticNode.vue';
  import FillTableCell from './FillTableCell.vue';
  import FillTitleNode from './FillTitleNode.vue';
  import { FILL_CONTEXT_KEY } from './fill-context';
  import { useMultipleTableRows } from './useMultipleTableRows';

  const props = defineProps<{ node: CompiledNode }>();
  const emit = defineEmits<{
    (e: 'update:value', fieldId: string, value: FieldValue): void;
    (e: 'update:cell', tableId: string, rowIndex: number, columnIndex: number, value: FieldValue): void;
    (e: 'add-row', tableId: string): void;
    (e: 'remove-row', tableId: string, rowIndex: number): void;
  }>();

  const context = inject(FILL_CONTEXT_KEY, null);
  const readOnly = computed(() => context?.readOnly.value === true);

  const { currentLogoUrl, handleLogoError } = useBranchLogo();

  const tableProperties = computed<ElementProperties>(() =>
    props.node.kind === 'table' ? props.node.element.properties : { name: '' },
  );
  const { containerStyles: tableContainerStyles } = useElementContainerStyles(tableProperties, 'TABLE');

  const extraGrid = computed(() =>
    props.node.kind === 'table' && props.node.isMultiple ? extraRowsGrid(props.node) : null,
  );
  const extraGridCells = computed(() => extraGrid.value?.cells ?? []);

  const { hasRemovableRow, atRowLimit, firstExtraColumn, extraCellKey, removeRow } = useMultipleTableRows(
    () => props.node,
    context,
    extraGridCells,
    (tableId, rowIndex) => emit('remove-row', tableId, rowIndex),
  );

  // A disabled control lets the click through to the field, whose own click handler selects it; the
  // cell selects the field only for a click around it, so "−" takes the row the person pointed at
  // anywhere in the cell, in a column they cannot edit too.
  function selectExtraCell(extra: ExtraGridCell, event: MouseEvent): void {
    const cellNode = extra.cell.node;
    if (cellNode?.kind !== 'field' || !cellNode.state.visible) return;
    if (event.target instanceof Element && event.target.closest('[data-fill-field]')) return;
    context?.onFocusField(cellNode.id);
  }

  function extraChange(extra: ExtraGridCell): ChangeHighlight | null {
    const node = extra.cell.node?.kind === 'field' ? extra.cell.node : null;
    return extraCellChange(context, props.node.id, extra.rowIndex, extra.columnIndex, node);
  }

  function forward(fieldId: string, value: FieldValue): void {
    emit('update:value', fieldId, value);
  }

  function forwardCell(tableId: string, rowIndex: number, columnIndex: number, value: FieldValue): void {
    emit('update:cell', tableId, rowIndex, columnIndex, value);
  }
</script>

<template>
  <FillFieldNode
    v-if="node.kind === 'field'"
    :node="node"
    @update:value="forward"
  />

  <FillStaticNode
    v-else-if="node.kind === 'static' && node.type === 'FIXED_TEXT'"
    :node="node"
  />

  <FillTitleNode
    v-else-if="node.kind === 'static'"
    :node="node"
  />

  <FillImageNode
    v-else-if="node.kind === 'image'"
    :node="node"
  />

  <div
    v-else-if="node.kind === 'header'"
    class="rounded-md border border-[var(--color-gray-02)] bg-white-01 [font-family:var(--font-base)]"
    :style="{
      marginTop: `${node.margin.top}px`,
      marginRight: `${node.margin.right}px`,
      marginBottom: `${node.margin.bottom}px`,
      marginLeft: `${node.margin.left}px`,
    }"
  >
    <table class="w-full table-fixed border-collapse">
      <colgroup>
        <col class="w-[18%]">
        <col class="w-[32%]">
        <col class="w-[50%]">
      </colgroup>
      <tbody>
        <tr>
          <td
            rowspan="2"
            class="border border-[var(--color-gray-02)] p-2 align-middle"
          >
            <div class="flex min-h-14 items-center justify-center p-2">
              <img
                v-if="currentLogoUrl"
                :src="currentLogoUrl"
                alt="Logo"
                class="max-h-14 w-full object-contain"
                @error="handleLogoError"
              >
            </div>
          </td>
          <td class="border border-[var(--color-gray-02)] p-2 text-center text-sm font-bold uppercase text-[var(--color-gray-07)]">
            {{ node.mainLabel }}
          </td>
          <td class="border border-[var(--color-gray-02)] p-2">
            <div class="flex w-full items-center gap-3">
              <div class="flex min-w-0 flex-1 items-center gap-1.5">
                <span class="shrink-0 text-xs font-bold text-[var(--color-gray-07)]">RQ</span>
                <span class="flex h-8 min-w-0 flex-1 items-center rounded-md border border-[var(--color-gray-02)] bg-white-01 px-2 py-1 text-xs text-[var(--color-gray-07)]">
                  {{ node.rq }}
                </span>
              </div>
              <div class="flex min-w-0 flex-1 items-center gap-1.5">
                <span class="shrink-0 text-xs font-bold text-[var(--color-gray-07)]">REV.</span>
                <span class="flex h-8 min-w-0 flex-1 items-center rounded-md border border-[var(--color-gray-02)] bg-white-01 px-2 py-1 text-xs text-[var(--color-gray-07)]">
                  {{ node.rev }}
                </span>
              </div>
            </div>
          </td>
        </tr>
        <tr>
          <td
            colspan="2"
            class="border border-[var(--color-gray-02)] p-2"
          >
            <div class="flex h-8 w-full items-center rounded-md border border-[var(--color-gray-02)] bg-white-01 px-2 py-1 text-left text-xs text-[var(--color-gray-07)]">
              {{ node.title }}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <div
    v-else-if="node.kind === 'table' && isNodeShown(node)"
    class="my-2"
    :style="tableContainerStyles"
    :data-testid="`fill-table-${node.id}`"
  >
    <div :style="fillGridStyles(extraGrid?.table ?? node)">
      <template
        v-for="row in node.cells"
        :key="row[0]?.id"
      >
        <FillTableCell
          v-for="cell in row.filter((item) => !item.hidden)"
          :key="cell.id"
          :table="extraGrid?.table ?? node"
          :cell="cell"
        >
          <FillNode
            v-if="cell.node"
            :node="cell.node"
            @update:value="forward"
            @update:cell="forwardCell"
            @add-row="emit('add-row', $event)"
            @remove-row="(tableId: string, rowIndex: number) => emit('remove-row', tableId, rowIndex)"
          />
        </FillTableCell>
      </template>
      <FillTableCell
        v-for="extra in extraGridCells"
        :key="extraCellKey(extra)"
        :table="extraGrid?.table ?? node"
        :cell="extra.cell"
        :change="extraChange(extra)"
        :extra-row-index="extra.rowIndex"
        :opens-row="extra.columnIndex === firstExtraColumn"
        :data-testid="`fill-table-extra-cell-${node.id}-${extra.rowIndex}-${extra.columnIndex}`"
        class="[&_:disabled]:pointer-events-none"
        @click="selectExtraCell(extra, $event)"
      >
        <FillFieldNode
          v-if="extra.cell.node?.kind === 'field'"
          :node="extra.cell.node"
          @update:value="(_fieldId: string, value: FieldValue) =>
            forwardCell(node.id, extra.rowIndex, extra.columnIndex, value)"
        />
      </FillTableCell>
    </div>

    <!-- A rule can take appending away after rows were added; "−" stays so they can be removed. -->
    <template v-if="!readOnly && node.isMultiple && (node.canAppend || hasRemovableRow)">
      <div :style="multipleTableBarRowStyles(node)">
        <Tooltip
          v-if="node.canAppend"
          :content="atRowLimit ? TABLE_NEW_ROWS_LIMIT_MESSAGE : 'Adicionar linha'"
          class="block w-full min-w-0"
        >
          <button
            type="button"
            class="flex h-6 w-full items-center justify-center rounded-lg border border-light-gray-xx bg-gray-08 text-sm leading-none shadow-xs"
            :class="atRowLimit
              ? 'cursor-not-allowed text-gray-16 opacity-50'
              : 'text-gray-03 hover:bg-gray-02'"
            :data-testid="`fill-table-add-row-${node.id}`"
            aria-label="Adicionar linha"
            :disabled="atRowLimit"
            @click="emit('add-row', node.id)"
          >
            +
          </button>
        </Tooltip>
        <Tooltip
          content="Remover linha"
          class="col-start-2 block w-full min-w-0"
        >
          <FillRemoveRowButton
            :table-id="node.id"
            :enabled="hasRemovableRow"
            @remove="removeRow"
          />
        </Tooltip>
      </div>
    </template>
  </div>

  <div
    v-else-if="node.kind === 'unsupported'"
    class="my-1 rounded-md border border-dashed border-amber-400 bg-amber-50 px-2 py-1 text-[10px] text-amber-800"
  >
    Tipo sem implementação no motor: {{ node.type }}
  </div>
</template>
